import React, { useState } from "react";
import { Scale, Loader2, RefreshCw, AlertTriangle, CheckCircle2, XCircle, FileText, Clock, Euro, Shield } from "lucide-react";
import { adminGenerateLegalAssessment } from "../lib/api";

const FOND_META = {
    alta: { label: "FONDATEZZA ALTA", color: "bg-emerald-700 text-white", bar: "bg-emerald-600" },
    media: { label: "FONDATEZZA MEDIA", color: "bg-amber-500 text-white", bar: "bg-amber-500" },
    bassa: { label: "FONDATEZZA BASSA", color: "bg-red-700 text-white", bar: "bg-red-600" },
    insufficiente_elementi: { label: "ELEMENTI INSUFFICIENTI", color: "bg-slate-600 text-white", bar: "bg-slate-500" },
};

const PARTE_LABEL = {
    lavoratore: "Lavoratore",
    datore_lavoro: "Datore di lavoro",
    non_chiaro: "Da chiarire",
};

export default function LegalAssessmentPanel({ contact, onUpdate }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const assessment = contact.legal_assessment;

    const run = async (force = false) => {
        setLoading(true);
        setError("");
        try {
            const res = await adminGenerateLegalAssessment(contact.id, force);
            onUpdate?.(contact.id, res);
        } catch (e) {
            setError(e?.response?.data?.detail || "Errore nella generazione della valutazione");
        } finally {
            setLoading(false);
        }
    };

    if (!assessment) {
        return (
            <div className="mt-4 border-t border-slate-200 pt-4">
                <button
                    onClick={() => run(false)}
                    disabled={loading}
                    data-testid={`btn-generate-assessment-${contact.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-navy/90 disabled:opacity-60"
                >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Scale size={14} />}
                    {loading ? "Analisi in corso (Claude Sonnet 5)…" : "Genera Valutazione Legale AI"}
                </button>
                {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
                <div className="mt-2 text-[11px] text-slate-500 italic">
                    Nota: la valutazione è una prima lettura legale interna basata su normativa italiana e orientamento giurisprudenziale consolidato. Non sostituisce lo studio approfondito del fascicolo.
                </div>
            </div>
        );
    }

    const fm = FOND_META[assessment.livello_fondatezza] || FOND_META.insufficiente_elementi;
    const score = assessment.punteggio_fondatezza ?? 0;

    return (
        <div className="mt-4 border-t border-slate-200 pt-4" data-testid={`assessment-${contact.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Scale size={16} className="text-navy" />
                    <div className="font-serif text-base text-navy font-bold">Valutazione Legale AI</div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                        {assessment.model || "claude-sonnet-5"}
                    </span>
                </div>
                <button
                    onClick={() => run(true)}
                    disabled={loading}
                    data-testid={`btn-regenerate-assessment-${contact.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wider text-slate-600 hover:border-navy hover:text-navy disabled:opacity-60"
                >
                    {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    Rigenera
                </button>
            </div>

            {/* Score bar + badge */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="col-span-2">
                    <div className="flex items-center justify-between mb-1">
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-bold ${fm.color}`}>
                            {fm.label}
                        </span>
                        <span className="font-mono text-sm text-navy font-bold">{score}/100</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full ${fm.bar} transition-all`} style={{ width: `${Math.max(2, score)}%` }} />
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <div className="px-2 py-1 bg-slate-100 border border-slate-200 text-slate-700">
                        <span className="text-[9px] uppercase tracking-wider text-slate-500 block">Parte</span>
                        <span className="font-semibold">{PARTE_LABEL[assessment.parte_assistita] || "n.d."}</span>
                    </div>
                    <div className="px-2 py-1 bg-slate-100 border border-slate-200 text-slate-700">
                        <span className="text-[9px] uppercase tracking-wider text-slate-500 block">Ambito</span>
                        <span className="font-semibold">{(assessment.ambito || "").replace(/_/g, " ")}</span>
                    </div>
                </div>
            </div>

            {/* Qualificazione */}
            <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gold-deep font-bold mb-1">Qualificazione giuridica</div>
                <div className="text-sm text-navy font-medium italic">{assessment.qualificazione_giuridica}</div>
            </div>

            {/* Sintesi */}
            <Section title="Sintesi dei fatti" icon={<FileText size={12} />}>
                <p className="whitespace-pre-wrap">{assessment.sintesi_fatti}</p>
            </Section>

            {/* Normativa + Giurisprudenza */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Section title="Normativa applicabile" icon={<Shield size={12} />}>
                    <List items={assessment.normativa_applicabile} mono />
                </Section>
                <Section title="Orientamento giurisprudenziale" icon={<Scale size={12} />}>
                    <List items={assessment.giurisprudenza_rilevante} />
                </Section>
            </div>

            {/* Pro / Contro */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <Section title="Elementi a favore" icon={<CheckCircle2 size={12} className="text-emerald-600" />}>
                    <List items={assessment.elementi_a_favore} tone="pro" />
                </Section>
                <Section title="Elementi a sfavore" icon={<XCircle size={12} className="text-red-600" />}>
                    <List items={assessment.elementi_a_sfavore} tone="contro" />
                </Section>
            </div>

            {/* Termini + Documenti */}
            <Section title="Termini di decadenza / prescrizione" icon={<Clock size={12} />}>
                <p className="text-amber-800 font-medium">{assessment.termini_decadenza_prescrizione}</p>
            </Section>

            <Section title="Documenti da richiedere al cliente" icon={<FileText size={12} />}>
                <List items={assessment.documenti_da_richiedere} />
            </Section>

            <Section title="Azioni consigliate" icon={<CheckCircle2 size={12} />}>
                <List items={assessment.azioni_consigliate} tone="action" />
            </Section>

            {/* Valore + Prudenza */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Section title="Stima valore contenzioso" icon={<Euro size={12} />}>
                    <p className="font-mono text-sm">{assessment.stima_valore_contenzioso}</p>
                </Section>
                <Section title="Note di prudenza" icon={<AlertTriangle size={12} className="text-amber-600" />}>
                    <p className="italic text-amber-900">{assessment.note_prudenza}</p>
                </Section>
            </div>

            <div className="mt-3 text-[10px] text-slate-500 italic">
                Valutazione generata il {assessment.generated_at ? new Date(assessment.generated_at).toLocaleString("it-IT") : "—"} · ad uso interno dello Studio.
            </div>
        </div>
    );
}

const Section = ({ title, icon, children }) => (
    <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gold-deep font-bold mb-1 flex items-center gap-1.5">
            {icon} {title}
        </div>
        <div className="text-xs text-slate-700 leading-relaxed">{children}</div>
    </div>
);

const List = ({ items, mono = false, tone = "" }) => {
    if (!items || items.length === 0) return <span className="text-slate-400 italic">—</span>;
    const toneCls = {
        pro: "border-l-emerald-400 bg-emerald-50/50",
        contro: "border-l-red-400 bg-red-50/50",
        action: "border-l-navy bg-slate-50",
    }[tone] || "border-l-slate-300 bg-slate-50";
    return (
        <ul className="space-y-1">
            {items.map((it, i) => (
                <li key={i} className={`pl-2 py-1 border-l-2 ${toneCls} ${mono ? "font-mono text-[11px]" : ""}`}>
                    {it}
                </li>
            ))}
        </ul>
    );
};
